import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  inject,
  output,
  viewChild,
  input,
  linkedSignal
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FileBrowserComponent } from '@app/_components/file-browser/file-browser.component';
import { FontBrowserComponent, SbFontSelection } from '@app/_components/font-browser/font-browser.component';
import { VariablesComponent } from '@app/_components/variables/variables.component';
import { isHelpPosition, resolveHelpText, SbHelpPosition } from '../sb-field-help';
import { SbRichTextComponent } from '../sb-rich-text/sb-rich-text.component';
import { RichTextTool, toolsFromToolbarGroups } from '../sb-rich-text/sb-rich-text-tools';
import { InfoPopoverComponent } from '../../info-popover/info-popover.component';
import { FileBrowserComponent as FileBrowserComponent_1 } from '../../file-browser/file-browser.component';
import { VariablesComponent as VariablesComponent_1 } from '../../variables/variables.component';
import { FontBrowserComponent as FontBrowserComponent_1 } from '../../font-browser/font-browser.component';

type ToolbarGroups = Array<{ name: string; items: string[] }>;

let nextId = 0;

@Component({
  selector: 'app-sb-input-textarea',
  templateUrl: './sb-textarea.component.html',
  styleUrls: ['./sb-textarea.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SbTextareaComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [InfoPopoverComponent, FileBrowserComponent_1, VariablesComponent_1, FontBrowserComponent_1, SbRichTextComponent]
})
export class SbTextareaComponent implements ControlValueAccessor, AfterViewInit {
  private readonly cdr = inject(ChangeDetectorRef);

  readonly label = input('');
  readonly description = input('');
  /** @deprecated Prefer `help`. Still used as fallback when `help` is empty. */
  readonly info = input('');
  readonly help = input('');
  readonly helpPosition = input<SbHelpPosition>('afterLabel');
  readonly placeholder = input('');
  readonly required = input(false);
  readonly disabledInput = input(false, {alias: 'disabled'});
  readonly disabled = linkedSignal(() => this.disabledInput());
  readonly error = input('');
  readonly id = input(`sb-textarea-${++nextId}`);
  readonly rows = input(3);
  readonly autoHeight = input(true);
  /** Optional flags. Prefer `options.autoExpand` to toggle auto-grow. */
  readonly options = input<{
    autoExpand?: boolean;
  } | null>(null);
  readonly wysiwyg = input(false);
  readonly showFileBrowser = input(false);
  readonly showVariableBrowser = input(false);
  readonly showFontBrowser = input(false);
  readonly clientId = input<number | null>(null);
  readonly projectId = input<number | null>(null);
  readonly variableClickFor = input('');
  readonly variableFieldType = input('html');
  readonly variableFormat = input('html');
  /** Toolbar groups of item names (sb-rich-text-tools); browser buttons are always prepended. */
  readonly toolbar = input<ToolbarGroups | null>(null);

  readonly fileBrowserClick = output<void>();
  readonly variableBrowserClick = output<void>();
  readonly fontBrowserClick = output<void>();
  readonly fileSelected = output<string>();
  readonly variableSelected = output<unknown>();
  readonly fontSelected = output<SbFontSelection>();

  readonly plainArea = viewChild<ElementRef<HTMLTextAreaElement>>('plainArea');
  readonly fileBrowser = viewChild<FileBrowserComponent>('fileBrowser');
  readonly fontBrowser = viewChild<FontBrowserComponent>('fontBrowser');
  readonly variablesBtn = viewChild<VariablesComponent>('variablesBtn');
  readonly richText = viewChild<SbRichTextComponent>('richText');

  value = '';
  private imageBrowsing = false;
  private tools: { key: string; tools: RichTextTool[] } | null = null;
  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  get helpText(): string {
    return resolveHelpText(this.help(), this.info());
  }
  get showHelpAfterLabel(): boolean {
    return isHelpPosition(this.helpText, this.helpPosition(), 'afterLabel');
  }
  get showHelpEndInside(): boolean {
    return isHelpPosition(this.helpText, this.helpPosition(), 'inputEndInside');
  }
  get showHelpEndOutside(): boolean {
    return isHelpPosition(this.helpText, this.helpPosition(), 'inputEndOutside');
  }
  get hasCompanions(): boolean {
    return !this.wysiwyg() && (
      this.showFileBrowser()
      || this.showVariableBrowser()
      || this.showFontBrowser()
      || this.showHelpEndInside
    );
  }

  get autoExpandEnabled(): boolean {
    const options = this.options();
    if (options && typeof options.autoExpand === 'boolean') {
      return options.autoExpand;
    }
    return this.autoHeight();
  }

  get inputPlaceholder(): string {
    return this.placeholder() || ' ';
  }

  get useFloatingPlaceholder(): boolean {
    return !!this.placeholder();
  }

  get resolvedVariableFor(): string {
    return this.variableClickFor() || this.id();
  }

  /** The default toolbar of the rich mode (the font browser in place of the font menu). */
  get richTextTools(): RichTextTool[] {
    const defaultToolbar: ToolbarGroups = [
      { name: 'basicstyles', items: ['Bold', 'Italic', 'Underline', 'Strike', '-', 'RemoveFormat'] },
      { name: 'paragraph', items: ['NumberedList', 'BulletedList', '-', 'Outdent', 'Indent'] },
      { name: 'links', items: ['Link', 'Unlink', 'Image'] },
      { name: 'styles', items: ['Format', 'Font', 'FontSize'] },
      { name: 'colors', items: ['TextColor', 'BGColor'] },
      { name: 'tools', items: ['Maximize'] },
    ];
    const groups: ToolbarGroups = [
      ...(this.showVariableBrowser() ? [{ name: 'sbBrowsers', items: ['SbVariable'] }] : []),
      ...(this.toolbar() || defaultToolbar),
    ];
    const key = JSON.stringify([groups, this.showFontBrowser()]);
    if (this.tools?.key !== key) {
      this.tools = { key, tools: toolsFromToolbarGroups(this.normalizeToolbar(groups)) };
    }
    return this.tools.tools;
  }

  ngAfterViewInit(): void {
    if (this.autoExpandEnabled && !this.wysiwyg()) {
      queueMicrotask(() => this.adjustPlainHeight());
    }
  }

  /** The forms API calls this and `setDisabledState` from outside the template (a `patchValue` in a subscribe). */
  writeValue(value: string): void {
    this.value = value ?? '';
    this.cdr.markForCheck();
    if (this.autoExpandEnabled && !this.wysiwyg()) {
      queueMicrotask(() => this.adjustPlainHeight());
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
    this.cdr.markForCheck();
  }

  onPlainInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.value = target.value;
    this.onChange(this.value);
    if (this.autoExpandEnabled) {
      this.adjustPlainHeight();
    }
  }

  onEditorChange(data: string): void {
    this.value = data ?? '';
    this.onChange(this.value);
  }

  openVariables(): void {
    const btn = this.variablesBtn();
    if (btn?.onAddVariablesButtonClick) {
      btn.onAddVariablesButtonClick(btn.variablesList());
    }
  }

  openFontBrowser(): void {
    this.fontBrowser()?.openFontBrowser();
  }

  /** The image panel's Browse Server: the file browser, for images. */
  openImageBrowser(): void {
    const fileBrowser = this.fileBrowser();
    if (!fileBrowser) {
      return;
    }
    this.imageBrowsing = true;
    fileBrowser.type.set('image');
    fileBrowser.openFileBrowser();
  }

  onBlur(): void {
    this.onTouched();
  }

  onFileBrowserSelected(url: string): void {
    this.fileBrowserClick.emit();
    const richText = this.richText();
    if (this.imageBrowsing && richText) {
      this.imageBrowsing = false;
      richText.setImageUrl(url);
    } else {
      this.insertIntoEditorOrValue(url);
    }
    this.fileSelected.emit(url);
  }

  onVariablePicked(variable: unknown): void {
    this.variableBrowserClick.emit();
    const text = this.extractVariableText(variable);
    this.insertIntoEditorOrValue(text);
    this.variableSelected.emit(variable);
  }

  onFontBrowserSelected(selection: SbFontSelection): void {
    this.fontBrowserClick.emit();
    const font = selection?.fontFamily || '';
    if (!font) {
      return;
    }
    this.fontSelected.emit(selection);
    const richText = this.richText();
    if (richText) {
      richText.setFontFamily(font, selection.fontFace);
      this.value = richText.currentHtml();
      this.onChange(this.value);
    } else {
      this.insertIntoEditorOrValue(font);
    }
    this.onTouched();
  }

  /** With the font browser, its button stands in for the font menu. */
  private normalizeToolbar(toolbar: ToolbarGroups): ToolbarGroups {
    if (!this.showFontBrowser()) {
      return toolbar;
    }
    return toolbar.map((group) => ({
      ...group,
      items: (group.items || []).map((item) => (item === 'Font' ? 'SbFont' : item)),
    }));
  }

  private insertIntoEditorOrValue(text: string): void {
    if (!text) {
      return;
    }
    const richText = this.richText();
    const plainArea = this.plainArea();
    if (richText) {
      richText.insertHtml(text);
      this.value = richText.currentHtml();
    } else if (plainArea?.nativeElement) {
      const el = plainArea.nativeElement;
      const start = el.selectionStart ?? this.value.length;
      const end = el.selectionEnd ?? this.value.length;
      this.value = `${this.value.substring(0, start)}${text}${this.value.substring(end)}`;
      queueMicrotask(() => {
        el.focus();
        const pos = start + text.length;
        el.setSelectionRange(pos, pos);
        if (this.autoExpandEnabled) {
          this.adjustPlainHeight();
        }
      });
    } else {
      this.value = `${this.value || ''}${text}`;
    }
    this.onChange(this.value);
    this.onTouched();
  }

  private extractVariableText(variable: unknown): string {
    if (variable == null) {
      return '';
    }
    if (typeof variable === 'string') {
      return variable;
    }
    const obj = variable as {
      key?: string;
      code?: string;
      value?: string;
      variable?: string;
      html?: string;
    };
    return obj.key || obj.html || obj.code || obj.value || obj.variable || '';
  }

  private adjustPlainHeight(): void {
    const el = this.plainArea()?.nativeElement;
    if (!el) {
      return;
    }
    el.style.height = 'auto';
    el.style.height = `${Math.max(el.scrollHeight, 72)}px`;
  }
}
